import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, ClipboardList, Pill, Calendar, BookOpen, LogOut } from "lucide-react";

export default function ProviderShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isRespite = user?.role === "respite";
  const allNav = [
    { to: "/provider", label: "Home", icon: LayoutDashboard, end: true },
    { to: "/provider/daily-note", label: "Daily Note", icon: ClipboardList },
    { to: "/provider/emar", label: "eMAR", icon: Pill },
    { to: "/provider/appointment", label: "Med/Dental", icon: Calendar, hideForRespite: true },
    { to: "/provider/resources", label: "Hub", icon: BookOpen, hideForRespite: true },
  ];
  const navItems = isRespite ? allNav.filter((n) => !n.hideForRespite) : allNav;

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-secondary/40 pb-24">
      <header className="bg-brand-navy text-white sticky top-0 z-30 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => !isRespite && navigate("/provider/household")}
              className="text-left"
              disabled={isRespite}
              data-testid="open-household"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">
                {isRespite ? "RESPITE PROVIDER ACCESS" : "OPPO CONTRACTOR PORTAL"}
              </p>
              <p className="font-display font-semibold text-sm leading-tight" data-testid="provider-name">{user?.name}</p>
              {!isRespite && <p className="text-[9px] opacity-70 mt-0.5">Tap to manage household →</p>}
            </button>
          </div>
          <Button
            variant="ghost" size="sm"
            onClick={handleLogout}
            className="text-white hover:bg-white/10 hover:text-white tap-target gap-1.5"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> <span className="text-xs font-semibold">Log out</span>
          </Button>
        </div>
        {isRespite && (
          <div className="bg-amber-500 text-white text-center text-xs py-1.5 font-semibold" data-testid="respite-banner">
            Respite Care Documentation Mode — scoped access window
          </div>
        )}
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-border z-40">
        <div className={`max-w-2xl mx-auto grid ${navItems.length === 3 ? "grid-cols-3" : "grid-cols-5"}`}>
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `tap-target flex flex-col items-center justify-center gap-1 py-2 text-xs transition-colors ${
                  isActive ? "text-brand-navy font-semibold" : "text-muted-foreground"
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "-")}`}
            >
              <Icon className="w-5 h-5" />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
